import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { fetchApi, fetchWithError } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import { selector } from "src/react-query/select";
import { alertSlice } from "src/store/alert/alert.slice";
import { bulkInvoiceExportRequest } from "src/react-query/endPoints";
import { is } from "src/util/is";
import enums from "src/util/enums";
import {
  IN_PROGRESS_STATUSES,
  POLL_INTERVAL_MS,
  VISIBLE_STATUSES,
} from "./invoiceExport.constants";
import { getExportErrorMessage } from "./invoiceExport.utils";

/**
 * Download Center list for the selected team.
 *
 * Polls only while at least one row is still PENDING/PROCESSING, then stops —
 * a list of finished exports has no reason to keep hitting the API. Polling is
 * inherently tab-scoped: a user who navigates away sees the updated status on
 * their next visit, which is consistent with the "no notification" decision.
 */
export const useInvoiceExports = ({ teamId, enabled = true }) => {
  const queryResult = useQuery(queryKeys.bulkInvoiceExports(teamId), fetchWithError, {
    enabled: enabled && !is.empty(teamId),
    select: selector,
    refetchInterval: (data) => {
      const rows = data?.requests ?? data ?? [];
      if (!Array.isArray(rows)) return false;
      const hasPending = rows.some((row) =>
        IN_PROGRESS_STATUSES.includes(row?.status)
      );
      return hasPending ? POLL_INTERVAL_MS : false;
    },
    refetchIntervalInBackground: false,
  });

  // The server already filters expired rows, but filtering here too means a
  // stale or over-broad response can't leak an EXPIRED row into the table.
  const rows = useMemo(() => {
    const raw = queryResult.data?.requests ?? queryResult.data ?? [];
    if (!Array.isArray(raw)) return [];
    return raw.filter((row) => VISIBLE_STATUSES.includes(row?.status));
  }, [queryResult.data]);

  return { ...queryResult, rows };
};

/**
 * Submit a new export request.
 *
 * Rate-limit rejections get a specific message rather than the generic error
 * text, so the user learns the actual constraint (2 per day).
 */
export const useCreateInvoiceExport = ({ teamId, onSuccess } = {}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    (payload) =>
      fetchApi({
        url: bulkInvoiceExportRequest(teamId),
        method: "POST",
        body: payload,
      }),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(queryKeys.bulkInvoiceExports(teamId));
        dispatch(
          alertSlice.actions.setAlert({
            open: true,
            severity: enums.SEVERITY.SUCCESS,
            message:
              "Your request is being processed. Please check the Download section in Transactions after some time.",
          })
        );
        onSuccess?.(data);
      },
      onError: (error) => {
        dispatch(
          alertSlice.actions.setAlert({
            open: true,
            severity: enums.SEVERITY.ERROR,
            message: getExportErrorMessage(error),
          })
        );
      },
    }
  );
};
